
import dagre from 'dagre';
import { AppNode } from '../types';
import { Edge, Position } from 'reactflow';

// Layout constants
const PARENT_WIDTH = 220;
const NODE_HEIGHT = 100;
const BOUNDARY_SIZE = 56;
const BOUNDARY_LABEL_H = 20;
const BOUNDARY_GAP = 12;
const PARENT_BOUNDARY_GAP = 30;
const DAGRE_NODESEP = 60;
const DAGRE_RANKSEP = 80;
const RANK_TOLERANCE = 5;
const GRID_SIZE = 15;

function snapToGrid(value: number): number {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

interface BoundingBox {
  width: number;
  height: number;
  offsets: Map<string, { x: number; y: number }>;
}

interface CandidateEdge {
  source: string;
  target: string;
  fromBoundary: boolean;
}

/**
 * Group boundary nodes by their parentNode ID.
 */
function buildBoundaryGroups(nodes: AppNode[]): Map<string, AppNode[]> {
  const groups = new Map<string, AppNode[]>();
  for (const node of nodes) {
    if (node.parentNode) {
      const list = groups.get(node.parentNode) || [];
      list.push(node);
      groups.set(node.parentNode, list);
    }
  }
  return groups;
}

/**
 * Calculate virtual bounding box dimensions and per-boundary offsets
 * for a parent node with attached boundary events.
 */
function calculateBoundingBox(
  boundaries: AppNode[],
  parentWidth: number,
  parentHeight: number
): BoundingBox {
  const count = boundaries.length;

  if (count === 0) {
    return {
      width: parentWidth,
      height: parentHeight,
      offsets: new Map(),
    };
  }

  const boundaryRowWidth = count * BOUNDARY_SIZE + (count - 1) * BOUNDARY_GAP;
  const boxWidth = Math.max(parentWidth, boundaryRowWidth + 20);
  const boxHeight = parentHeight + PARENT_BOUNDARY_GAP + BOUNDARY_SIZE + BOUNDARY_LABEL_H;

  const rowStartX = (parentWidth - boundaryRowWidth) / 2;
  const boundaryY = parentHeight + PARENT_BOUNDARY_GAP;

  const offsets = new Map<string, { x: number; y: number }>();
  for (let i = 0; i < count; i++) {
    const x = rowStartX + i * (BOUNDARY_SIZE + BOUNDARY_GAP);
    offsets.set(boundaries[i].id, { x, y: boundaryY });
  }

  return { width: boxWidth, height: boxHeight, offsets };
}

/**
 * Detect back-edges using DFS from start-event nodes.
 */
function detectBackEdges(
  startIds: string[],
  adjacency: Map<string, string[]>
): Set<string> {
  const backEdges = new Set<string>();
  const visited = new Set<string>();
  const inStack = new Set<string>();

  function dfs(nodeId: string) {
    visited.add(nodeId);
    inStack.add(nodeId);

    for (const neighbor of adjacency.get(nodeId) || []) {
      if (inStack.has(neighbor)) {
        backEdges.add(`${nodeId}->${neighbor}`);
      } else if (!visited.has(neighbor)) {
        dfs(neighbor);
      }
    }

    inStack.delete(nodeId);
  }

  for (const startId of startIds) {
    if (!visited.has(startId)) dfs(startId);
  }
  for (const nodeId of adjacency.keys()) {
    if (!visited.has(nodeId)) dfs(nodeId);
  }

  return backEdges;
}

/**
 * Post-layout: reorder Y positions within ranks so that direct successors
 * of a parent sit above boundary successors. Propagates the reordering
 * through all subsequent ranks via barycenter recalculation.
 */
function correctBoundaryTargetOrdering(
  topLevelIds: Set<string>,
  boundaryGroups: Map<string, AppNode[]>,
  candidateEdges: CandidateEdge[],
  backEdges: Set<string>,
  positions: Map<string, { x: number; y: number }>
): void {
  // Build direct / boundary successor maps
  const directSucc = new Map<string, string[]>();
  const boundarySucc = new Map<string, string[]>();

  for (const edge of candidateEdges) {
    if (backEdges.has(`${edge.source}->${edge.target}`)) continue;
    const map = edge.fromBoundary ? boundarySucc : directSucc;
    const list = map.get(edge.source) || [];
    if (!list.includes(edge.target)) list.push(edge.target);
    map.set(edge.source, list);
  }

  // Identify boundary targets that compete with direct targets from the same parent
  const boundaryTargetIds = new Set<string>();

  let hasInversions = false;
  for (const [parentId] of boundaryGroups) {
    if (!topLevelIds.has(parentId)) continue;
    const dTargets = directSucc.get(parentId) || [];
    const bTargets = boundarySucc.get(parentId) || [];
    if (dTargets.length === 0 || bTargets.length === 0) continue;

    bTargets.forEach((id) => boundaryTargetIds.add(id));

    const dMaxY = Math.max(...dTargets.map((id) => positions.get(id)?.y ?? 0));
    const bMinY = Math.min(...bTargets.map((id) => positions.get(id)?.y ?? 0));
    if (bMinY <= dMaxY) hasInversions = true;
  }

  if (!hasInversions) return;

  // Group nodes into ranks by X proximity
  const sortedByX = [...positions.entries()].sort((a, b) => a[1].x - b[1].x);
  const ranks: Array<{ x: number; nodes: string[] }> = [];
  const nodeToRankIdx = new Map<string, number>();

  for (const [id, pos] of sortedByX) {
    const last = ranks[ranks.length - 1];
    if (last && Math.abs(pos.x - last.x) < RANK_TOLERANCE) {
      last.nodes.push(id);
    } else {
      ranks.push({ x: pos.x, nodes: [id] });
    }
    nodeToRankIdx.set(id, ranks.length - 1);
  }

  // Build predecessor map for barycenter computation
  const predMap = new Map<string, string[]>();
  for (const edge of candidateEdges) {
    if (backEdges.has(`${edge.source}->${edge.target}`)) continue;
    const list = predMap.get(edge.target) || [];
    if (!list.includes(edge.source)) list.push(edge.source);
    predMap.set(edge.target, list);
  }

  // Find earliest rank containing an inverted boundary target
  let firstAffectedIdx = ranks.length;
  for (const bt of boundaryTargetIds) {
    const idx = nodeToRankIdx.get(bt);
    if (idx !== undefined && idx < firstAffectedIdx) firstAffectedIdx = idx;
  }

  // Reorder from the first affected rank through all subsequent ranks
  for (let i = firstAffectedIdx; i < ranks.length; i++) {
    const nodesInRank = ranks[i].nodes;
    if (nodesInRank.length <= 1) continue;

    // Compute barycenter for each node from predecessors' current Y
    const entries = nodesInRank.map((id) => {
      const preds = predMap.get(id) || [];
      let bc: number;
      if (preds.length === 0) {
        bc = positions.get(id)!.y;
      } else {
        bc =
          preds.reduce((s, pid) => s + (positions.get(pid)?.y ?? 0), 0) /
          preds.length;
      }
      // Tiebreaker: boundary targets get a slight downward bias so they
      // sort below direct targets that share the same predecessor
      if (boundaryTargetIds.has(id)) bc += 0.1;
      return { id, bc };
    });

    entries.sort((a, b) => a.bc - b.bc);

    // Collect the original Y slots for this rank (sorted ascending)
    const ySlots = nodesInRank
      .map((id) => positions.get(id)!.y)
      .sort((a, b) => a - b);

    // Reassign Y positions in the new barycenter order
    for (let j = 0; j < entries.length; j++) {
      positions.get(entries[j].id)!.y = ySlots[j];
    }
  }
}

export const getLayoutedElements = (
  nodes: AppNode[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'LR'
): AppNode[] => {
  // ── Phase 1: Analysis ──

  const topLevelNodes: AppNode[] = [];
  const topLevelIds = new Set<string>();

  for (const node of nodes) {
    if (!node.parentNode) {
      topLevelNodes.push(node);
      topLevelIds.add(node.id);
    }
  }

  const boundaryGroups = buildBoundaryGroups(nodes);

  const boundaryToParent = new Map<string, string>();
  for (const node of nodes) {
    if (node.parentNode) {
      boundaryToParent.set(node.id, node.parentNode);
    }
  }

  const startIds = topLevelNodes
    .filter((n) => n.type?.includes('StartEvent'))
    .map((n) => n.id);

  const adjacency = new Map<string, string[]>();
  for (const id of topLevelIds) {
    adjacency.set(id, []);
  }

  // Classify edges, tracking boundary origin for post-layout correction
  const candidateEdges: CandidateEdge[] = [];

  for (const edge of edges) {
    if (edge.sourceHandle === 'boundary') continue;

    const sourceIsBoundary = boundaryToParent.has(edge.source);
    const targetIsBoundary = boundaryToParent.has(edge.target);

    const effectiveSource = sourceIsBoundary
      ? boundaryToParent.get(edge.source)!
      : edge.source;
    const effectiveTarget = targetIsBoundary
      ? boundaryToParent.get(edge.target)!
      : edge.target;

    if (topLevelIds.has(effectiveSource) && topLevelIds.has(effectiveTarget)) {
      if (effectiveSource === effectiveTarget) continue;
      candidateEdges.push({
        source: effectiveSource,
        target: effectiveTarget,
        fromBoundary: sourceIsBoundary,
      });

      const neighbors = adjacency.get(effectiveSource);
      if (neighbors) neighbors.push(effectiveTarget);
    }
  }

  const backEdges = detectBackEdges(startIds, adjacency);

  // Filter and deduplicate for Dagre
  const dagreEdgeSet = new Set<string>();
  const dagreEdges: Array<{ source: string; target: string }> = [];

  for (const edge of candidateEdges) {
    const key = `${edge.source}->${edge.target}`;
    if (backEdges.has(key)) continue;
    if (dagreEdgeSet.has(key)) continue;
    dagreEdgeSet.add(key);
    dagreEdges.push(edge);
  }

  // ── Phase 2: Virtual Bounding Boxes ──

  const boundingBoxes = new Map<string, BoundingBox>();

  for (const node of topLevelNodes) {
    const measuredWidth = node.width ?? PARENT_WIDTH;
    const measuredHeight = node.height ?? NODE_HEIGHT;
    const boundaries = boundaryGroups.get(node.id);
    if (boundaries && boundaries.length > 0) {
      boundingBoxes.set(
        node.id,
        calculateBoundingBox(boundaries, measuredWidth, measuredHeight)
      );
    } else {
      boundingBoxes.set(node.id, {
        width: measuredWidth,
        height: measuredHeight,
        offsets: new Map(),
      });
    }
  }

  // ── Phase 3: Dagre Layout ──

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: DAGRE_NODESEP,
    ranksep: DAGRE_RANKSEP,
  });

  for (const node of topLevelNodes) {
    const box = boundingBoxes.get(node.id)!;
    dagreGraph.setNode(node.id, { width: box.width, height: box.height });
  }

  for (const edge of dagreEdges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  // ── Phase 3.5: Post-layout Y-order correction ──
  // Reorder nodes within ranks so that direct successors of a parent
  // are above boundary successors, then propagate via barycenter recalc.

  const correctedPositions = new Map<string, { x: number; y: number }>();
  for (const node of topLevelNodes) {
    const dn = dagreGraph.node(node.id);
    if (dn) correctedPositions.set(node.id, { x: dn.x, y: dn.y });
  }

  correctBoundaryTargetOrdering(
    topLevelIds,
    boundaryGroups,
    candidateEdges,
    backEdges,
    correctedPositions
  );

  // ── Phase 4: Position Assignment ──

  return nodes.map((node) => {
    if (node.parentNode) {
      const parentBox = boundingBoxes.get(node.parentNode);
      if (parentBox) {
        const offset = parentBox.offsets.get(node.id);
        if (offset) {
          return { ...node, position: { x: snapToGrid(offset.x), y: snapToGrid(offset.y) } };
        }
      }
      return node;
    }

    const pos = correctedPositions.get(node.id);
    if (!pos) return node;

    const box = boundingBoxes.get(node.id)!;

    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: {
        x: snapToGrid(pos.x - box.width / 2),
        y: snapToGrid(pos.y - box.height / 2),
      },
    };
  });
};
